import React, { createContext, useContext, useRef } from 'react';
import { I18n } from 'lib/I18n';

// react-rails mounts each component in isolation, so there is no ancestor to
// supply React context. This HOC re-creates that: it takes a `context` prop off
// the mounted component and exposes it to descendants through a Provider.
//
// Historically this used the legacy `childContextTypes`/`getChildContext` API
// (removed in React 19). It is rewritten on the modern Context API. No current
// consumer reads the context value directly — the only load-bearing effect is
// syncing `I18n.locale` — but the Provider is kept so descendants can opt in via
// `useTrailheadContext()` without another react-rails bridge.
export const TrailheadContext = createContext({});

export function useTrailheadContext() {
  return useContext(TrailheadContext);
}

export default function provideContext(Component) {
  return function contextWrapper(propsWithContext) {
    const { context = {}, ...props } = propsWithContext;

    // Match the legacy once-per-mount side effect (previously in
    // UNSAFE_componentWillMount) without re-running it on every render.
    const localeApplied = useRef(false);
    if (!localeApplied.current) {
      if (context.locale) I18n.locale = context.locale;
      localeApplied.current = true;
    }

    return (
      <TrailheadContext.Provider value={context}>
        <Component {...props} />
      </TrailheadContext.Provider>
    );
  };
}
